import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';

export default function AbandonedIntents() {
     const [intents, setIntents] = useState([]);
     const [filter, setFilter] = useState('in_progress');
     const [loading, setLoading] = useState(true);

     useEffect(() => {
          fetchIntents();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [filter]);

     async function fetchIntents() {
          setLoading(true);
          let query = supabase
               .from('booking_intents')
               .select('*')
               .order('updated_at', { ascending: false });

          if (filter !== 'all') {
               query = query.eq('status', filter);
          }

          // Only show intents that have at least an email
          query = query.not('customer_email', 'is', null);

          const { data } = await query;
          setIntents(data || []);
          setLoading(false);
     }

     async function markAbandoned(id) {
          await supabase
               .from('booking_intents')
               .update({ status: 'abandoned', updated_at: new Date().toISOString() })
               .eq('id', id);
          fetchIntents();
     }

     const filters = ['in_progress', 'abandoned', 'all'];
     const stepLabels = ['Equipment', 'Contact Info', 'Select Dates', 'Review & Pay'];

     return (
          <div>
               <h1 className='text-darker text-2xl font-bold mb-2'>Abandoned Bookings</h1>
               <p className='text-darker/40 text-sm mb-6'>
                    Visitors who started booking but didn't complete payment.
               </p>

               {/* Filters */}
               <div className='flex gap-2 mb-6 flex-wrap'>
                    {filters.map((f) => (
                         <button
                              key={f}
                              onClick={() => setFilter(f)}
                              className={`px-3 py-1.5 rounded-lg text-sm capitalize duration-300 ${
                                   filter === f
                                        ? 'bg-yellow/10 text-yellow border border-yellow'
                                        : 'text-darker/40 border border-gray-200 hover:text-darker'
                              }`}
                         >
                              {f === 'in_progress' ? 'In Progress' : f}
                         </button>
                    ))}
               </div>

               <div className='bg-white rounded-xl border border-gray-200 overflow-hidden'>
                    {loading ? (
                         <p className='text-darker/40 p-4'>Loading...</p>
                    ) : intents.length === 0 ? (
                         <p className='text-darker/40 p-4'>No abandoned bookings found.</p>
                    ) : (
                         <div className='divide-y divide-gray-200'>
                              {intents.map((intent) => (
                                   <div
                                        key={intent.id}
                                        className='p-4 hover:bg-gray-100 duration-300'
                                   >
                                        <div className='flex flex-col tablet:flex-row items-start tablet:items-center justify-between gap-3'>
                                             <div className='flex-1'>
                                                  <div className='flex items-center gap-2 flex-wrap'>
                                                       <p className='text-darker font-semibold'>
                                                            {intent.customer_name || 'Unknown'}
                                                       </p>
                                                       <span className={`px-2 py-0.5 rounded text-xs ${
                                                            intent.status === 'in_progress'
                                                                 ? 'bg-orange-500/20 text-orange-300'
                                                                 : 'bg-red-500/20 text-red-300'
                                                       }`}>
                                                            {intent.status === 'in_progress' ? 'In Progress' : 'Abandoned'}
                                                       </span>
                                                       <span className='text-darker/20 text-xs'>
                                                            Stopped at: {stepLabels[intent.current_step] || `Step ${intent.current_step}`}
                                                       </span>
                                                  </div>
                                                  <div className='flex items-center gap-4 mt-1 text-sm'>
                                                       {intent.customer_email && (
                                                            <a
                                                                 href={`mailto:${intent.customer_email}`}
                                                                 className='text-yellow hover:underline'
                                                            >
                                                                 {intent.customer_email}
                                                            </a>
                                                       )}
                                                       {intent.customer_phone && (
                                                            <a
                                                                 href={`tel:${intent.customer_phone}`}
                                                                 className='text-darker/40 hover:text-darker'
                                                            >
                                                                 {intent.customer_phone}
                                                            </a>
                                                       )}
                                                  </div>
                                                  {intent.customer_address && (
                                                       <p className='text-darker/40 text-xs mt-1'>
                                                            {intent.customer_address}, {intent.customer_city}, {intent.customer_state} {intent.customer_zip}
                                                       </p>
                                                  )}
                                                  <div className='flex items-center gap-4 mt-1 text-xs text-darker/30'>
                                                       {intent.equipment_description && (
                                                            <span>{intent.equipment_description}</span>
                                                       )}
                                                       {intent.start_date && (
                                                            <span>
                                                                 {intent.start_date} to {intent.end_date}
                                                            </span>
                                                       )}
                                                       {intent.total_cents && (
                                                            <span className='text-yellow/50'>
                                                                 ${(intent.total_cents / 100).toLocaleString()}
                                                            </span>
                                                       )}
                                                       <span>
                                                            {format(new Date(intent.updated_at), 'MMM d, yyyy h:mm a')}
                                                       </span>
                                                  </div>
                                             </div>
                                             <div className='flex gap-2'>
                                                  {intent.status === 'in_progress' && (
                                                       <button
                                                            onClick={() => markAbandoned(intent.id)}
                                                            className='px-3 py-1.5 rounded-lg text-xs text-darker/40 border border-gray-200 hover:text-darker duration-300'
                                                       >
                                                            Mark Abandoned
                                                       </button>
                                                  )}
                                             </div>
                                        </div>
                                   </div>
                              ))}
                         </div>
                    )}
               </div>
          </div>
     );
}
